/* eslint-disable max-lines-per-function */
import { type PayloadAction, createAction } from '@reduxjs/toolkit';
import { logger } from 'lib/logger';
import { getUserShort } from 'reactApp/api/axios.corsapi';
import { SendVkMusicPromocodeAPICall } from 'reactApp/api/billing/SendVkMusicPromocodeAPICall';
import { IS_BIZ_USER, IS_CORP_USER, IS_PAID_USER } from 'reactApp/appHelpers/configHelpers';
import {
    vkMusicAccountLink,
    vkMusicBundleEnabled,
    vkMusicBundleQuotaLimit,
    vkMusicBundleTrialTariff,
    vkMusicBundleUsualTariff,
    vkMusicBundleVKIDChecks,
} from 'reactApp/appHelpers/featuresHelpers/features/vkMusicBundle';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { loadUserQuotaFailure, loadUserQuotaSuccess } from 'reactApp/modules/userQuota/userQuota.module';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { loadUserSocialBindsIfNotLoaded } from 'reactApp/modules/userSocialBinds/userSocialBinds.provider';
import { UserSocialBindSelectors } from 'reactApp/modules/userSocialBinds/userSocialBinds.selectors';
import { VK_MUSIC_BUNDLE_DISABLED, VK_MUSIC_BUNDLE_PROMO_ID, vkMusicBundleLogger } from 'reactApp/modules/vkMusic/vkMusic.const';
import {
    type BuyVkMusicBundleAction,
    type BuyVkMusicBundleChooseAction,
    VkMusicBundleActions,
} from 'reactApp/modules/vkMusic/vkMusic.module';
import { goToPromoQuota } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { store as reduxStore } from 'reactApp/store';
import {
    closeVkMusicConfirmModal,
    renderVkMusicAnonymousConfirmModal,
    renderVkMusicConfirmModal,
} from 'reactApp/ui/VkMusic/VkMusicConfirmModal/VkMusicConfirmModal.helpers';
import { VkMusicErrorModalType } from 'reactApp/ui/VkMusic/VkMusicErrorModal/VkMusicErrorModal';
import { closeVkMusicErrorModal, renderVkMusicErrorModal } from 'reactApp/ui/VkMusic/VkMusicErrorModal/VkMusicErrorModal.helpers';
import { renderVkMusicSuccessModal } from 'reactApp/ui/VkMusic/VkMusicSuccessModal/VkMusicSuccessModal.helpers';
import { ConditionsChecker } from 'reactApp/utils/conditionChecker';
import { sendXray } from 'reactApp/utils/ga';
import opener from 'reactApp/utils/opener';
import { captureException } from 'reactApp/utils/tracer';
import { doSilentVkAuth, doVisibleVkAuth } from 'reactApp/utils/vkidSdk';
import { channel } from 'redux-saga';
import { put, race, take, takeEvery } from 'redux-saga/effects';
import { call, select } from 'typed-redux-saga';

import { checkPossibility, CheckPossibilityStatus, openVkMusicBundleBuyModal } from './vkMusic.helpers';
import { VkMusicBundleSelectors } from './vkMusic.selectors';
import { type VKIDInfo, VkMusicBundleVkidSource } from './vkMusic.types';

const sendPromocodeApi = () => new SendVkMusicPromocodeAPICall().makeRequest();

export const sendVkMusicPromocodeRequest = createAction('sendVkMusicPromocodeRequest');

export function* handleSendPromocodeRequest() {
    try {
        yield sendPromocodeApi();
    } catch (error) {
        logger.error(error);
        captureException(error, { issueKey: 'sendVkMusicPromocode/error' });
        showSnackbarAction({
            text: 'Не удалось отправить промокод',
            type: SnackbarTypes.failure,
            id: 'SEND_VK_MUSIC_PROMOCODE_FAILURE',
            closable: true,
        });
    }
}

/**
 * Собирает общую для всех точек входа в покупку информацию
 * @returns {string} canOffer - можем ли предложить юзеру оффер бандла
 * @returns {VkMusicBundleTariffInfo} tariffInfo - инфо о тарифе, который можем предложить
 * @returns {VKIDInfo | undefined} vkid - найденный (возможно) c помощью getAvailableVKID вкид,
 * к которому можно предложить подключить Музыку
 * @returns {CheckPossibilityStatus} mailSpaceCheckStatus - результат проверки доступности бандла на нашей стороне
 * @returns {CheckPossibilityStatus} vkMusicCheckStatus - результат проверки доступности бандла на стороне Музыки
 *
 * canOffer и tariffInfo считаются на основе пары (mailSpaceCheckStatus, vkMusicCheckStatus),
 * но сами статусы на всякий случай возвращаем тоже
 */
export function* getVkMusicBundleInfo() {
    const showCount: number = storeHelper.getValue(VK_MUSIC_BUNDLE_PROMO_ID) || 0;
    const offerDisabled = storeHelper.getValue(VK_MUSIC_BUNDLE_DISABLED);

    // если успел увидеть оффер, а потом купил подписку (эту или другую – неважно),
    // больше оффер не показываем
    if (showCount && IS_PAID_USER) {
        storeHelper.setValue(VK_MUSIC_BUNDLE_DISABLED, true);
    }

    const conditions = new ConditionsChecker((conditionsScope, list) => {
        vkMusicBundleLogger.info(`${conditionsScope} conditions failure`, list);
    });

    // "статические" условия,
    // для проверки которых не нужны запросы
    if (
        !conditions.addWithCheck('static', {
            isFeatureFlagDisabled: !vkMusicBundleEnabled,
            IS_BIZ_USER,
            IS_CORP_USER,
            IS_PAID_USER,
            offerDisabled: !!offerDisabled,
        })
    ) {
        return { canOffer: false };
    }

    // для проверки условия на квоту
    const { isLoaded: isUserQuotaLoaded } = yield* select(UserQuotaSelectors.getLoadingState);
    if (!isUserQuotaLoaded) {
        const { fail } = yield race({
            success: take(loadUserQuotaSuccess),
            fail: take(loadUserQuotaFailure),
        });

        if (fail) {
            return { canOffer: false };
        }
    }
    const { used: usedPercent } = yield* select(UserQuotaSelectors.getFilledPercents);
    const isQuotaLimit = usedPercent < vkMusicBundleQuotaLimit;

    if (
        !conditions.addWithCheck('quota', {
            isNotQuotaLimit: !isQuotaLimit,
        })
    ) {
        return { canOffer: false };
    }

    let vkid = yield* getAvailableVKID();
    const { mailSpaceCheckStatus, vkMusicCheckStatus } = yield* checkPossibility(vkid);

    if (
        !conditions.addWithCheck('check-possibility', {
            mailSpaceCheckStatusFail: [CheckPossibilityStatus.ERROR, CheckPossibilityStatus.NOT_AVAILABLE].includes(mailSpaceCheckStatus),
            vkMusicCheckStatusFail: [CheckPossibilityStatus.ERROR, CheckPossibilityStatus.NOT_AVAILABLE].includes(vkMusicCheckStatus),
        })
    ) {
        return { canOffer: false, vkMusicCheckStatus, mailSpaceCheckStatus };
    }

    vkMusicBundleLogger.info('check success', conditions.getList());

    const tariffInfo =
        mailSpaceCheckStatus === CheckPossibilityStatus.TRIAL && vkMusicCheckStatus === CheckPossibilityStatus.TRIAL
            ? vkMusicBundleTrialTariff
            : vkMusicBundleUsualTariff;

    if (vkid?.vkidSource === VkMusicBundleVkidSource.login) {
        const success = yield loadUserSocialBindsIfNotLoaded();
        if (!success) {
            return { canOffer: false };
        }
        const bindedVkid = yield* select(UserSocialBindSelectors.getVkId);
        if (bindedVkid) {
            vkid = {
                ...vkid,
                login: Number(bindedVkid.loginParsed),
                firstName: bindedVkid.firstName || '',
                lastName: bindedVkid.lastName || '',
            };
        }
    }

    return { canOffer: true, tariffInfo, vkid, mailSpaceCheckStatus, vkMusicCheckStatus };
}

/**
 * Использует разные эвристики для подбора вкида, который можем предложить
 * В порядке приоритета:
 * - соц юзер VKID – отдаем его родной вкид (он также не сможет на поздних этапах выбрать другой)
 * - есть привязка VKID к акку и входили через неё – отдаем прязанный VKID
 * - есть активная сессия VKID в браузере (независимо от продуктов Mail) – отдаем вкид из неё
 * - есть привязка VKID (не обязательно со входом через неё) – отдаем привязанный VKID
 * - иначе – отдаем undefined
 */
export function* getAvailableVKID() {
    const isVKIDSocialUser = yield* select(UserSelectors.isVKIDSocialUser);
    const isSocialUser = yield* select(UserSelectors.isSocialUser);

    const { social, login, silent, bind } = vkMusicBundleVKIDChecks;
    const checks = {
        social: social && isVKIDSocialUser,
        login: login && !isSocialUser,
        silent,
        bind: bind && !isSocialUser,
    };

    if (checks.social) {
        const login = yield* select(UserSelectors.getLogin);
        const name = yield* select(VkMusicBundleSelectors.getFullName);
        const [firstName, lastName] = name.split(' ');

        return {
            vkidSource: VkMusicBundleVkidSource.social,
            login: Number(login),
            firstName,
            lastName,
        };
    }

    if (checks.login) {
        try {
            sendXray(['user_short_fetch', 'vkm25']);
            const userShortData = yield* call(getUserShort);

            if (userShortData?.vkc_user_id) {
                return {
                    vkidSource: VkMusicBundleVkidSource.login,
                    login: Number(userShortData.vkc_user_id),
                    firstName: '',
                    lastName: '',
                };
            }
        } catch (_) {
            sendXray(['user_short_error', 'vkm25']);
        }
    }

    if (checks.silent) {
        const authRes = yield* call(doSilentVkAuth);

        if (authRes?.user) {
            const { user } = authRes;

            return {
                vkidSource: VkMusicBundleVkidSource.silent,
                login: user.id,
                firstName: user.first_name,
                lastName: user.last_name,
                phone: user.phone,
                avatar: user.avatar,
            };
        }
    }

    if (checks.bind) {
        yield loadUserSocialBindsIfNotLoaded();
        const vkid = yield* select(UserSocialBindSelectors.getVkId);

        if (vkid) {
            return {
                vkidSource: VkMusicBundleVkidSource.bind,
                login: Number(vkid.loginParsed) || 0,
                firstName: vkid.firstName,
                lastName: vkid.lastName,
            };
        }
    }

    return undefined;
}

/**
 * Дополнительный флоу покупки бандла с явным входом в нужный вкид
 * @param baseTariffInfo - какой тариф предлагали юзеру изначально
 * @param onChoose - что делать после успешного входа во вкид (например, закрыть изначальную модалку оффера)
 * @param needConfirm - нужна ли модалка вида "Нужно выбрать вкид, на который подключим Музыку"
 * @param source - точка входа во флоу покупки (для радаров)
 */
export function* handleBuyVkMusicBundleChoose(action: PayloadAction<BuyVkMusicBundleChooseAction>) {
    const { baseTariffInfo, onChoose, source, needConfirm = false } = action?.payload;
    const flowChannel = channel<boolean>();

    if (needConfirm) {
        renderVkMusicAnonymousConfirmModal({
            onClick: () => {
                flowChannel.put(true);
            },
            onCancel: () => {
                flowChannel.close();
            },
            source,
        });
    } else {
        flowChannel.put(true);
    }

    let flowStepSuccess = yield take(flowChannel);
    if (!flowStepSuccess) {
        return;
    }

    const { SDKErrorCodes } = yield import('@vkontakte/superappkit');

    // даем юзеру явно войти во вкид через отдельное окно
    let vkid: VKIDInfo;
    try {
        const authRes = yield* call(doVisibleVkAuth);
        if (!authRes || !authRes.user) {
            throw { code: SDKErrorCodes.UNKNOWN };
        }
        const { user } = authRes;
        vkMusicBundleLogger.info('choose vkid', authRes);
        vkid = {
            vkidSource: VkMusicBundleVkidSource.choosen,
            login: user.id,
            firstName: user.first_name,
            lastName: user.last_name,
            phone: user.phone,
            avatar: user.avatar,
        };
        onChoose?.();
    } catch (err) {
        const code = (err as { code: number })?.code;
        sendXray(['vkid_sdk_auth_error', 'code', String(code)]);
        if (![SDKErrorCodes.CONNECT_WINDOW_CLOSED, SDKErrorCodes.UNKNOWN].includes(code)) {
            yield put(
                showSnackbarAction({
                    type: SnackbarTypes.failure,
                    id: 'vkid choose error',
                    text: 'Произошла ошибка',
                    closable: true,
                })
            );
        }
        return;
    }

    const { mailSpaceCheckStatus, vkMusicCheckStatus } = yield* checkPossibility(vkid);

    if (vkMusicCheckStatus === CheckPossibilityStatus.ERROR) {
        yield put(
            showSnackbarAction({
                type: SnackbarTypes.failure,
                id: 'check-posibility error',
                text: 'Произошла ошибка',
                closable: true,
            })
        );
        return;
    }

    const chooseAnotherVKID = () => {
        reduxStore.dispatch(
            VkMusicBundleActions.buyVkMusicBundleChoose({
                baseTariffInfo,
                baseVKID: null,
                onChoose: () => {
                    closeVkMusicErrorModal();
                    flowChannel.close();
                },
                source,
            })
        );
    };

    // Оказалось, что на выбранный вкид недоступен бандл вообще (ни триал, ни обычный)
    if (vkMusicCheckStatus === CheckPossibilityStatus.NOT_AVAILABLE) {
        renderVkMusicErrorModal({
            type: VkMusicErrorModalType.NOT_AVAILABLE_ALL,
            onPrimaryClick: () => {
                flowChannel.put(true);
            },
            onSecondaryClick: chooseAnotherVKID,
            onCancel: () => {
                flowChannel.close();
            },
            source,
        });

        const flowStepSuccess = yield take(flowChannel);
        if (!flowStepSuccess) {
            return;
        }

        goToPromoQuota({
            currentPage: true,
            query: PaymentUTM.vkMusicBundle,
        });

        return;
    }

    const tariffInfo =
        mailSpaceCheckStatus === CheckPossibilityStatus.TRIAL && vkMusicCheckStatus === CheckPossibilityStatus.TRIAL
            ? vkMusicBundleTrialTariff
            : vkMusicBundleUsualTariff;

    // Когда изначально предлагали триал, а оказалось, что на выбранный вкид доступен только обычный бандл
    if (vkMusicCheckStatus === CheckPossibilityStatus.USUAL && baseTariffInfo.isTrial) {
        renderVkMusicErrorModal({
            type: VkMusicErrorModalType.NOT_AVAILABLE_TRIAL,
            onPrimaryClick: () => {
                flowChannel.put(true);
            },
            onSecondaryClick: chooseAnotherVKID,
            onCancel: () => {
                flowChannel.close();
            },
            tariffInfo,
            source,
        });
    } else {
        flowChannel.put(true);
    }

    flowStepSuccess = yield take(flowChannel);
    if (!flowStepSuccess) {
        return;
    }

    // Нашли вкид и показали нужные модалки – возвращаемся в основной флоу
    reduxStore.dispatch(
        VkMusicBundleActions.buyVkMusicBundle({
            tariffInfo,
            vkid,
            needConfirm: false,
            source,
        })
    );
}

/**
 * Основная часть флоу покупки бандла, когда смогли зарезолвить вкид заранее
 * @param tariffInfo - какой тариф покупаем
 * @param vkid - заранее найденный вкид, для которого подключаем Музыку
 * @param needConfirm - нужна ли модалка вида "Подтвердите вкид для подключения Музыки"
 * или сразу показываем окно оплаты
 * @param source - точка входа во флоу покупки (для радаров)
 */
export function* handleBuyVkMusicBundle(action: PayloadAction<BuyVkMusicBundleAction>) {
    const { tariffInfo, vkid, needConfirm, source } = action?.payload || {};
    const email = yield* select(UserSelectors.getEmail);
    const fullName = yield* select(VkMusicBundleSelectors.getFullName);
    const isSocialVKID = vkid.vkidSource === VkMusicBundleVkidSource.social;
    const flowChannel = channel<boolean>();

    if (needConfirm) {
        renderVkMusicConfirmModal({
            // социальным юзерам не даем выбрать не свой вкид, чтобы не было путаницы
            canChoose: !isSocialVKID,
            onChooseClick: async () => {
                reduxStore.dispatch(
                    VkMusicBundleActions.buyVkMusicBundleChoose({
                        baseTariffInfo: tariffInfo,
                        baseVKID: vkid,
                        onChoose: () => {
                            closeVkMusicConfirmModal();
                            flowChannel.close();
                        },
                        source,
                    })
                );
            },
            mailUser: isSocialVKID
                ? undefined
                : {
                      email: email || '',
                      fullName,
                  },
            vkidUser: vkid,
            onClick: () => {
                flowChannel.put(true);
            },
            onCancel: () => {
                flowChannel.close();
            },
            source,
        });
    } else {
        flowChannel.put(true);
    }

    let flowStepSuccess = yield take(flowChannel);
    if (!flowStepSuccess) {
        return;
    }

    let isSuccess = false;

    openVkMusicBundleBuyModal({
        onSuccess: () => {
            isSuccess = true;
        },
        onClose: () => {
            if (isSuccess) {
                flowChannel.put(true);
                return;
            }
            flowChannel.close();
        },
        tariffInfo,
        vkid,
        source,
    });

    flowStepSuccess = yield take(flowChannel);
    if (!flowStepSuccess) {
        return;
    }

    renderVkMusicSuccessModal({
        onPrimaryClick: () => {
            opener(vkMusicAccountLink);
        },
        tariffInfo,
        source,
    });
}

export function* handleLoadVkMusicBundleInfo() {
    const data = yield* getVkMusicBundleInfo();

    yield put(VkMusicBundleActions.getVkMusicBundleInfoSuccess(data));
}

export function* watchVkMusicRoot() {
    yield takeEvery(VkMusicBundleActions.loadVkMusicBundleInfo.toString(), handleLoadVkMusicBundleInfo);
    yield takeEvery(VkMusicBundleActions.buyVkMusicBundle.toString(), handleBuyVkMusicBundle);
    yield takeEvery(VkMusicBundleActions.buyVkMusicBundleChoose.toString(), handleBuyVkMusicBundleChoose);
    yield takeEvery(sendVkMusicPromocodeRequest.toString(), handleSendPromocodeRequest);
}
